import type { ReactNode } from 'react';
import {
  parseEntityRef,
  RELATION_OWNED_BY,
  RELATION_PART_OF,
} from '@backstage/catalog-model';
import { InfoCard, Link } from '@backstage/core-components';
import { EntityRefLink } from '@backstage/plugin-catalog-react';
import { Box, makeStyles } from '@material-ui/core';
import { EDIT_URL } from './CatalogDefectsBanner';
import { useComponentPage } from './ComponentPageContext';
import { targetsOf } from './relations';
import { UnresolvedBadge } from './UnresolvedBadge';

const useStyles = makeStyles(theme => ({
  list: { margin: 0 },
  row: {
    display: 'grid',
    gridTemplateColumns: '7rem minmax(0, 1fr)',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 0),
    borderBottom: '1px solid var(--ui-border)',
    '&:last-child': { borderBottom: 'none' },
  },
  term: { color: 'var(--ui-text-muted)' },
  value: { margin: 0, minWidth: 0, overflowWrap: 'anywhere' },
}));

/**
 * Ownership & metadata (specs/component-page.md): who owns the component and
 * where it sits. Owner, system, lifecycle and type are the catalog fields that
 * exist today; tier, on-call and runtime join once they are defined (DX-359).
 */
export const OwnershipCard = () => {
  const classes = useStyles();
  const { entity, catalog } = useComponentPage();
  const spec = (entity.spec ?? {}) as Record<string, unknown>;
  const owner = targetsOf(entity, RELATION_OWNED_BY)[0];
  const system = targetsOf(entity, RELATION_PART_OF).find(
    ref => parseEntityRef(ref).kind.toLowerCase() === 'system',
  );
  const editUrl = entity.metadata.annotations?.[EDIT_URL];

  const rows: [string, ReactNode][] = [];
  if (owner) {
    rows.push([
      'Owner',
      <>
        <EntityRefLink entityRef={owner} disableTooltip />
        {catalog.unresolved.includes(owner) && <UnresolvedBadge />}
      </>,
    ]);
  }
  if (system) {
    rows.push(['System', <EntityRefLink entityRef={system} disableTooltip />]);
  }
  if (typeof spec.lifecycle === 'string')
    rows.push(['Lifecycle', spec.lifecycle]);
  if (typeof spec.type === 'string') rows.push(['Type', spec.type]);

  return (
    <InfoCard
      title="Ownership & metadata"
      action={
        editUrl ? (
          <Box pt={1} pr={1}>
            <Link to={editUrl}>Edit</Link>
          </Box>
        ) : undefined
      }
    >
      {/* Named on a wrapper: a role on the dl itself would cost its terms
          and definitions their meaning to assistive technology. */}
      <div role="group" aria-label="Ownership & metadata">
        <dl className={classes.list}>
          {rows.map(([term, value]) => (
            <div key={term} className={classes.row}>
              <dt className={classes.term}>{term}</dt>
              <dd className={classes.value}>{value}</dd>
            </div>
          ))}
        </dl>
      </div>
    </InfoCard>
  );
};
